import { ref } from 'vue'
import { getValidateToken } from '@/api/requests/UserRequests'
import { useAuthStore } from '@/stores/login'
import router from '@/router'
import { isApp, skipLoginScreen } from '@/utils/client'
import { isTudoApp, closeWebview } from '@/utils/tudo'
import { isOutFieldApp, outFieldCloseWebview } from '@/utils/outfield'
import qs from 'qs'
import { isFeishu } from '@/utils/feishu'
import { isSandsTalkFlutterApp } from '@/utils/sandsTalkFlutter'
import { isSandsTalkMSFlutterApp } from '@/utils/sandsTalkMSFlutter'
import { isSandsTalkNativeApp } from '@/utils/sandsTalkNative'
import { isSandsTalkPhOutfieldApp } from '@/utils/sandsTalkPhOutfiled'
import { isBranchManagerApp } from '@/utils/sandsTalkBranchManager'
import { isWisdomRabbitApp } from '@/utils/sandsTalkSmartRabbit'
import { UserInfo } from '@/api/Models/UserInfo'
import { isMobile } from '@/utils/Device'
import { getQueryString } from '@/utils'

const avoidLoginpath: string[] = [
  '/previewVideo',
  '/broadcastDetail',
  '/comDetailLevel',
  '/PrivacyPolicy',
  '/UserAgreement',
  '/surveyInfo',
  '/surveyDetails',
  '/qiweiTransit',
  '/collectedInformation',
  '/unsupported'
]

let timer: number | null = null
const skipLoginPage = async () => {
  if (isApp) {
    skipLoginScreen()
  } else if (isTudoApp) {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      closeWebview()
    }, 1000)
  } else if (isOutFieldApp) {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      outFieldCloseWebview()
    }, 1000)
  } else {
    const authStore = useAuthStore()
    const { setUserId } = authStore
    setUserId('')
    const path = location.href.split('#')[1]
    if (!isMobile()) {
      if (path.indexOf('/enterpriseLogin') === -1) {
        router.replace('/login?redirect=' + encodeURIComponent(encodeURIComponent(path)))
      }
    } else {
      if (
        path.indexOf('/login') === -1 &&
        path.indexOf('/enterpriseLogin') === -1 &&
        path.indexOf('/phoneLogin') === -1
      ) {
        // 根据地址中是否包含'saas'来决定跳转页面
        const currentUrl = window.location.href
        if (currentUrl.includes('saas')) {
          router.replace('/login?redirect=' + encodeURIComponent(encodeURIComponent(path)))
        } else {
          router.replace('/enterpriseLogin?redirect=' + encodeURIComponent(encodeURIComponent(path)))
        }
      }
    }
  }
}

const showCompany = (userInfo: UserInfo): boolean => {
  const list = userInfo.tenantConfigList
  for (let i = 0; i < list.length; i++) {
    if (list[i].code === 'COMPANY_MGMT' && list[i].value === '1') {
      return true
    }
  }
  return false
}

const showLive = (userInfo: UserInfo): boolean => {
  const list = userInfo.tenantConfigList
  for (let i = 0; i < list.length; i++) {
    if (list[i].code === 'LIVE_MODULE' && list[i].value === '1') {
      return true
    }
  }
  return false
}

export const useValidateToken = () => {
  const validateShow = ref(false)

  // cn_driver_app 来源时跳过登录页重定向，交由入口免登处理
  const isCnDriverAppSource = () => getQueryString('source') === 'cn_driver_app'

  if (isApp || isTudoApp || isFeishu || isOutFieldApp || isCnDriverAppSource()) {
    validateShow.value = true
    return validateShow
  }
  const currentPath = '/' + window.location.hash.split('#')[1].split('?')[0].split('/')[1]
  // console.log(currentPath, 'currentPath==========', window.location.hash)
  const appQuery = qs.parse(window.location.hash.split('?')[1], { ignoreQueryPrefix: true })
  if (avoidLoginpath.indexOf(currentPath) > -1) {
    if (currentPath === '/comDetailLevel' && appQuery.isPublic !== '1') {
      validateShow.value = false
    } else {
      validateShow.value = true
      return validateShow
    }
  }

  const sandsTalkToken = localStorage.getItem('sandsTalkToken')
  if (!sandsTalkToken) {
    validateShow.value = true
    skipLoginPage()
    return validateShow
  }

  const validateToken = async () => {
    const authStore = useAuthStore()
    const {
      setTeamStudy,
      setDeptId,
      setShowCompany,
      setHasHistoryData,
      setRoleCodes,
      setName,
      setNickName,
      setPostName,
      setDeptName,
      setDeptFullName
    } = authStore
    setTeamStudy(0)
    const { code, data } = await getValidateToken()
    if (code === 1) {
      setTeamStudy(data?.sysUserLabel?.teamLearningLabel as number)
      setDeptId(data?.deptId as string)
      setShowCompany(showCompany(data))
      // 根据 hasHistoryData 字段设置状态
      setHasHistoryData(showLive(data))
      setRoleCodes(data.roleCodes || [])
      // 设置水印相关字段
      setName(data.name || '')
      setNickName(data.nickName || '')
      setPostName(data.postName || '')
      setDeptName(data.deptName || '')
      setDeptFullName(data.deptFullName || '')
      // 将完整用户信息存储到 localStorage 中，供水印使用
      const userInfoForWatermark = {
        name: data.name || '',
        username: data.username || '',
        nickName: data.nickName || '',
        postName: data.postName || '',
        deptName: data.deptName || '',
        deptFullName: data.deptFullName || ''
      }
      localStorage.setItem('SandsTalkUserInfo', JSON.stringify(userInfoForWatermark))
      validateShow.value = true
    }
    const loginExpirseCodeArr = [14100, 14280]
    if (loginExpirseCodeArr.indexOf(code) > -1) {
      skipLoginPage()
    }
    validateShow.value = true
  }

  if (
    !isSandsTalkFlutterApp &&
    !isSandsTalkMSFlutterApp &&
    !isSandsTalkNativeApp &&
    !isSandsTalkPhOutfieldApp &&
    !isBranchManagerApp &&
    !isWisdomRabbitApp
  ) {
    validateToken()
  } else {
    // 印尼、菲律宾外场不调用current-info
    validateShow.value = true
  }

  return validateShow
}
